import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  BarChart3, 
  Clock, 
  Star, 
  ArrowRight,
  CheckCircle,
  Zap,
  Shield,
  Globe,
  MessageSquare,
  FileText,
  DollarSign,
  Mail
} from 'lucide-react';
import { 
  KPIDashboardSnapshot, 
  TeamSnapshot, 
  MessagesSnapshot, 
  ProposalsSnapshot, 
  CallingSnapshot, 
  CalendarSnapshot 
} from './components/FeatureSnapshots';

const Website: React.FC = () => {
  const navigate = useNavigate();
  
  // Animation state for cycling business types
  const businessTypes = [
    'Detailers',
    'Vinyl Wrap Studios', 
    'House Cleaning',
    'Home Services',
    'Lawn Care',
    'Contractors',
    'Ecommerce',
    'Field Services'
  ];
  
  const [currentTypeIndex, setCurrentTypeIndex] = useState(0);
  const [nextTypeIndex, setNextTypeIndex] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Scroll animation states
  const [headerVisible, setHeaderVisible] = useState(false);
  const [card1Visible, setCard1Visible] = useState(false);
  const [card2Visible, setCard2Visible] = useState(false);
  const [card3Visible, setCard3Visible] = useState(false);
  
  const headerRef = useRef<HTMLDivElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);
  const card3Ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (currentTypeIndex + 1) % businessTypes.length;
      setNextTypeIndex(nextIndex);
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentTypeIndex(nextIndex);
        setIsAnimating(false);
      }, 600); // Complete animation cycle
      
    }, 3000); // Change every 3 seconds
    
    return () => clearInterval(interval);
  }, [currentTypeIndex, businessTypes.length]);

  // Intersection Observers for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const headerObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHeaderVisible(true);
      }
    }, observerOptions);

    const card1Observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setCard1Visible(true), 200);
      }
    }, observerOptions);

    const card2Observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setCard2Visible(true), 400);
      }
    }, observerOptions);

    const card3Observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => setCard3Visible(true), 600);
      }
    }, observerOptions);

    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (card1Ref.current) card1Observer.observe(card1Ref.current);
    if (card2Ref.current) card2Observer.observe(card2Ref.current);
    if (card3Ref.current) card3Observer.observe(card3Ref.current);

    return () => {
      if (headerRef.current) headerObserver.unobserve(headerRef.current);
      if (card1Ref.current) card1Observer.unobserve(card1Ref.current);
      if (card2Ref.current) card2Observer.unobserve(card2Ref.current);
      if (card3Ref.current) card3Observer.unobserve(card3Ref.current);
    };
  }, []);

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="relative z-50 px-6 py-6 bg-black">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <span className="text-3xl font-bold">Nexus</span>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-10">
            <a href="#home" className="text-lg text-gray-300 hover:text-white transition-colors">Home</a>
            <a href="#about" className="text-lg text-gray-300 hover:text-white transition-colors">About</a>
            <a href="#features" className="text-lg text-gray-300 hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="text-lg text-gray-300 hover:text-white transition-colors">Pricing</a>
            <a href="#contact" className="text-lg text-gray-300 hover:text-white transition-colors">Contact</a>
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center space-x-5">
            <button
              onClick={handleSignIn}
              className="text-lg text-gray-300 hover:text-white transition-colors"
            >
              Sign in
            </button>
            <button
              onClick={handleGetStarted}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white text-base font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105"
            >
              Get started
            </button>
          </div>
        </div>
      </nav>

        {/* Hero Section */}
        <section id="home" className="relative px-6 py-8 lg:py-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-black"></div>
        
        {/* Floating Background Shapes */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="text-left px-4 lg:px-0">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 lg:mb-8 leading-tight">
                <span className="block text-white">Bookings</span>
                <span className="block text-white">Made Easy for</span>
                <div className="relative h-20 sm:h-24 md:h-32 lg:h-36 w-full">
                  <div className="absolute inset-0 flex items-start justify-start">
                    {/* Current word - falls out */}
                    <div 
                      className={`
                        text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold 
                        bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent
                        transition-all duration-300 ease-in transform
                        whitespace-nowrap absolute left-0 top-0
                        ${isAnimating 
                          ? 'animate-[dropOut_0.3s_ease-in_forwards]' 
                          : 'translate-y-0 opacity-100'
                        }
                      `}
                    >
                      {businessTypes[currentTypeIndex]}
                    </div>
                    
                    {/* Next word - drops in */}
                    {isAnimating && (
                      <div 
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold 
                                   bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent
                                   animate-[dropIn_0.5s_ease-out_0.1s_forwards] opacity-0
                                   whitespace-nowrap absolute left-0 top-0"
                      >
                        {businessTypes[nextTypeIndex]}
                      </div>
                    )}
                  </div>
                </div>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-300 mb-8 lg:mb-12 leading-relaxed max-w-2xl">
                <span className="font-bold text-white">Turbocharge your side-hustle or full scale enterprise:</span> effortless scheduling, auto-pilot comms, and client wow-moments that turn one-offs into loyal legions of repeat business.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 lg:mb-16">
                <button
                  onClick={handleGetStarted}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white text-base sm:text-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <span>Start Free Trial</span>
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent border border-gray-600 rounded-full text-white text-base sm:text-lg font-semibold hover:bg-gray-800/50 transition-all duration-300">
                  Book Demo
                </button>
              </div>
            </div>

            {/* Right Column - Visual */}
            <div className="relative lg:text-right flex items-start justify-end w-full h-full hidden lg:block">
              {/* Vertical Moving Carousel */}
              <div className="relative w-full max-w-2xl h-[600px] overflow-hidden mt-0 lg:ml-16 xl:ml-24">
                {/* Left Column - Moving Up */}
                <div className="absolute left-0 w-1/3 h-full">
                  <div className="animate-[scrollUp_20s_linear_infinite] flex flex-col space-y-4">
                    {/* Duplicate images for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop" alt="Auto Detailing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Auto Detailing</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop" alt="House Cleaning" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">House Cleaning</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop" alt="Lawn Care" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Lawn Care</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop" alt="Contractors" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Contractors</span>
                      </div>
                    </div>
                    {/* Duplicate for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop" alt="Auto Detailing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Auto Detailing</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop" alt="House Cleaning" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">House Cleaning</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop" alt="Lawn Care" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Lawn Care</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop" alt="Contractors" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Contractors</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle Column - Moving Down */}
                <div className="absolute left-1/3 w-1/3 h-full">
                  <div className="animate-[scrollDown_25s_linear_infinite] flex flex-col space-y-4">
                    {/* Duplicate images for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&h=300&fit=crop" alt="Field Services" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Field Services</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=400&h=300&fit=crop" alt="Vinyl Wrap" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Vinyl Wrap</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop" alt="Home Services" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Home Services</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop" alt="Ecommerce" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Ecommerce</span>
                      </div>
                    </div>
                    {/* Duplicate for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&h=300&fit=crop" alt="Field Services" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Field Services</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=400&h=300&fit=crop" alt="Vinyl Wrap" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Vinyl Wrap</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop" alt="Home Services" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Home Services</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop" alt="Ecommerce" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Ecommerce</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Moving Up (same speed as left) */}
                <div className="absolute right-0 w-1/3 h-full">
                  <div className="animate-[scrollUp_20s_linear_infinite] flex flex-col space-y-4">
                    {/* Duplicate images for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=400&h=300&fit=crop" alt="Plumbing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Plumbing</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=300&fit=crop" alt="Electrical" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Electrical</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop" alt="HVAC" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">HVAC</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop" alt="Roofing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Roofing</span>
                      </div>
                    </div>
                    {/* Duplicate for seamless loop */}
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=400&h=300&fit=crop" alt="Plumbing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Plumbing</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=300&fit=crop" alt="Electrical" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Electrical</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop" alt="HVAC" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">HVAC</span>
                      </div>
                    </div>
                    <div className="w-full h-40 rounded-lg overflow-hidden relative">
                      <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop" alt="Roofing" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold">Roofing</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gradient Overlays for fade effect */}
                <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-gray-900 to-transparent pointer-events-none z-10"></div>
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none z-10"></div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-8 mt-8 max-w-4xl mx-auto px-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1 md:mb-2">24.3k</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-400 leading-tight">Businesses Rely On Nexus</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent mb-1 md:mb-2">500+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-400 leading-tight">Hours Saved Monthly</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-1 md:mb-2">98%</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-400 leading-tight">Customer Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent mb-1 md:mb-2">35%</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-400 leading-tight">Average Revenue Increase</div>
            </div>
          </div>
        </div>
      </section>

        {/* Integrations Section */}
        <section className="px-6 pt-8 py-20 pb-32 bg-gray-900 relative overflow-hidden">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-600/25 via-pink-500/20 to-orange-500/30"></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Nexus Connect
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Seemlessly integrate your most vital tools and apps
              </p>
            </div>
          
          {/* Flowing Wire Streams Background */}
          <div className="relative h-96 max-w-6xl mx-auto">
            {/* Desktop Wire Stream SVG - Flowing from left to right */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none hidden md:block" viewBox="0 0 800 400">
              <defs>
                <linearGradient id="wireGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
                  <stop offset="50%" stopColor="#ffffff" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0.1" />
                </linearGradient>
              </defs>
              
              {/* Chaotic wire streams - starting from single point, spreading wildly */}
              {/* Wild upward streams */}
              <path d="M 50 200 Q 80 160, 120 140 Q 180 100, 240 80 Q 320 40, 380 20 Q 460 -10, 520 10 Q 600 30, 680 5 Q 760 -20, 800 -5" 
                    stroke="url(#wireGradient)" strokeWidth="1.8" fill="none" opacity="0.6" />
              <path d="M 50 200 Q 90 180, 140 120 Q 200 60, 280 90 Q 350 120, 420 70 Q 500 20, 580 50 Q 660 80, 740 30 Q 780 10, 800 25" 
                    stroke="url(#wireGradient)" strokeWidth="1.2" fill="none" strokeDasharray="10,6" opacity="0.5" className="animate-wire-flow-fast" />
              <path d="M 50 200 Q 100 170, 160 130 Q 230 90, 300 110 Q 380 130, 450 85 Q 530 40, 610 65 Q 690 90, 770 45 Q 790 35, 800 50" 
                    stroke="url(#wireGradient)" strokeWidth="0.8" fill="none" opacity="0.4" />
              
              {/* Chaotic middle streams */}
              <path d="M 50 200 Q 110 190, 180 210 Q 260 230, 340 190 Q 420 150, 500 170 Q 580 190, 660 150 Q 740 110, 800 130" 
                    stroke="url(#wireGradient)" strokeWidth="2.8" fill="none" opacity="0.8" />
              <path d="M 50 200 Q 95 220, 150 240 Q 220 260, 290 220 Q 370 180, 440 200 Q 520 220, 600 180 Q 680 140, 760 160 Q 780 170, 800 155" 
                    stroke="url(#wireGradient)" strokeWidth="1.6" fill="none" strokeDasharray="14,8" opacity="0.7" className="animate-wire-flow" />
              <path d="M 50 200 Q 120 215, 190 195 Q 270 175, 350 205 Q 430 235, 510 195 Q 590 155, 670 185 Q 750 215, 800 180" 
                    stroke="url(#wireGradient)" strokeWidth="1.4" fill="none" opacity="0.6" />
              
              {/* Erratic horizontal streams */}
              <path d="M 50 200 Q 130 205, 210 190 Q 290 175, 370 195 Q 450 215, 530 185 Q 610 155, 690 175 Q 770 195, 800 175" 
                    stroke="url(#wireGradient)" strokeWidth="2.2" fill="none" opacity="0.7" />
              <path d="M 50 200 Q 140 210, 230 225 Q 320 240, 410 215 Q 500 190, 590 210 Q 680 230, 770 205 Q 785 200, 800 195" 
                    stroke="url(#wireGradient)" strokeWidth="1.3" fill="none" strokeDasharray="12,4" opacity="0.6" className="animate-wire-flow-slow" />
              
              {/* Wild downward streams */}
              <path d="M 50 200 Q 85 240, 125 280 Q 185 340, 245 320 Q 325 300, 385 340 Q 465 380, 525 350 Q 605 320, 685 360 Q 765 400, 800 375" 
                    stroke="url(#wireGradient)" strokeWidth="2.0" fill="none" opacity="0.6" />
              <path d="M 50 200 Q 105 250, 165 290 Q 235 330, 315 310 Q 395 290, 475 320 Q 555 350, 635 330 Q 715 310, 795 340" 
                    stroke="url(#wireGradient)" strokeWidth="1.5" fill="none" strokeDasharray="8,5" opacity="0.5" className="animate-wire-flow-fast" />
              <path d="M 50 200 Q 115 260, 175 300 Q 245 340, 325 320 Q 405 300, 485 330 Q 565 360, 645 340 Q 725 320, 800 350" 
                    stroke="url(#wireGradient)" strokeWidth="1.0" fill="none" opacity="0.4" />
              
              {/* Spiraling streams */}
              <path d="M 50 200 Q 120 180, 190 200 Q 260 220, 330 180 Q 400 140, 470 180 Q 540 220, 610 160 Q 680 100, 750 140 Q 775 160, 800 120" 
                    stroke="url(#wireGradient)" strokeWidth="1.7" fill="none" strokeDasharray="6,4" opacity="0.5" className="animate-wire-flow" />
              
              {/* Gentle chaotic streams */}
              <path d="M 50 200 Q 110 185, 170 215 Q 230 245, 290 205 Q 350 165, 410 195 Q 470 225, 530 185 Q 590 145, 650 175 Q 710 205, 770 165 Q 785 155, 800 170" 
                    stroke="url(#wireGradient)" strokeWidth="2.4" fill="none" opacity="0.7" />
              <path d="M 50 200 Q 125 195, 200 220 Q 275 245, 350 210 Q 425 175, 500 200 Q 575 225, 650 190 Q 725 155, 800 180" 
                    stroke="url(#wireGradient)" strokeWidth="1.9" fill="none" strokeDasharray="16,6" opacity="0.6" className="animate-wire-flow-slow" />
            </svg>
            
            {/* Mobile Wire Stream SVG - Flowing from bottom to top */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none md:hidden" viewBox="0 0 400 1200" style={{ height: '150%', top: '-25%' }}>
              <defs>
                <linearGradient id="wireGradientMobile" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.4" />
                  <stop offset="30%" stopColor="#ffffff" stopOpacity="0.3" />
                  <stop offset="60%" stopColor="#ffffff" stopOpacity="0.15" />
                  <stop offset="80%" stopColor="#ffffff" stopOpacity="0.05" />
                  <stop offset="95%" stopColor="#ffffff" stopOpacity="0.01" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>
              
              {/* Mobile upward flowing streams - Extended to flow behind title */}
              <path d="M 200 1100 Q 160 1050, 140 980 Q 100 880, 80 780 Q 40 650, 20 520 Q -10 380, 10 240 Q 30 100, 5 -40 Q -20 -180, -5 -300 Q 10 -420, -10 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.2" fill="none" opacity="0.3" />
              <path d="M 200 1100 Q 180 1040, 120 960 Q 60 860, 90 720 Q 120 580, 70 440 Q 20 300, 50 160 Q 80 20, 30 -120 Q 10 -260, 25 -380 Q 40 -480, 20 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="1.8" fill="none" strokeDasharray="10,6" opacity="0.25" className="animate-wire-flow-fast" />
              <path d="M 200 1100 Q 190 1040, 210 920 Q 230 780, 190 640 Q 150 500, 170 360 Q 190 220, 150 80 Q 110 -60, 130 -200 Q 150 -340, 120 -460 Q 90 -500, 110 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.8" fill="none" opacity="0.4" />
              <path d="M 200 1100 Q 220 1035, 240 950 Q 260 820, 220 690 Q 180 560, 200 430 Q 220 300, 180 170 Q 140 40, 160 -100 Q 170 -240, 155 -380 Q 140 -480, 160 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.0" fill="none" strokeDasharray="14,8" opacity="0.35" className="animate-wire-flow" />
              <path d="M 200 1100 Q 215 1050, 195 990 Q 175 870, 205 750 Q 235 630, 195 510 Q 155 390, 185 270 Q 215 150, 175 30 Q 135 -90, 165 -230 Q 195 -370, 175 -480 Q 155 -500, 175 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="1.9" fill="none" opacity="0.3" />
              <path d="M 200 1100 Q 205 1060, 190 1010 Q 175 920, 195 830 Q 215 740, 185 650 Q 155 560, 175 470 Q 195 380, 165 290 Q 135 200, 155 110 Q 175 20, 145 -70 Q 115 -160, 135 -250 Q 155 -340, 125 -430 Q 95 -500, 115 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.5" fill="none" opacity="0.35" />
              <path d="M 200 1100 Q 210 1070, 225 1030 Q 240 940, 215 850 Q 190 760, 210 670 Q 230 580, 205 490 Q 180 400, 200 310 Q 220 220, 195 130 Q 170 40, 190 -50 Q 210 -140, 185 -230 Q 160 -320, 180 -410 Q 200 -480, 180 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="1.7" fill="none" strokeDasharray="12,4" opacity="0.3" className="animate-wire-flow-slow" />
              <path d="M 200 1100 Q 240 1045, 280 975 Q 340 865, 320 745 Q 300 625, 340 505 Q 380 385, 350 265 Q 320 145, 360 25 Q 400 -95, 375 -215 Q 350 -335, 380 -455 Q 410 -500, 375 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.4" fill="none" opacity="0.3" />
              <path d="M 200 1100 Q 250 1035, 290 965 Q 330 855, 310 745 Q 290 635, 320 525 Q 350 415, 330 305 Q 310 195, 340 85 Q 370 -25, 345 -135 Q 320 -245, 340 -355 Q 360 -465, 340 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.1" fill="none" strokeDasharray="8,5" opacity="0.25" className="animate-wire-flow-fast" />
              <path d="M 200 1100 Q 185 1045, 215 970 Q 245 855, 205 740 Q 165 625, 195 510 Q 225 395, 185 280 Q 145 165, 175 50 Q 205 -65, 165 -180 Q 125 -295, 145 -410 Q 165 -500, 145 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.3" fill="none" strokeDasharray="6,4" opacity="0.25" className="animate-wire-flow" />
              <path d="M 200 1100 Q 195 1015, 220 920 Q 245 805, 210 690 Q 175 575, 200 460 Q 225 345, 190 230 Q 155 115, 180 0 Q 205 -115, 170 -230 Q 135 -345, 160 -460 Q 185 -500, 170 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.6" fill="none" opacity="0.35" />
              <path d="M 200 1100 Q 195 1055, 220 1000 Q 245 885, 210 775 Q 175 665, 200 555 Q 225 445, 190 335 Q 155 225, 180 115 Q 205 5, 170 -105 Q 135 -215, 160 -325 Q 185 -435, 150 -500" 
                    stroke="url(#wireGradientMobile)" strokeWidth="2.3" fill="none" strokeDasharray="16,6" opacity="0.3" className="animate-wire-flow-slow" />
            </svg>
            
            {/* PWA App Icon Hub - Desktop: left side, Mobile: bottom center */}
            <div className="absolute transform animate-float-subtle z-20 hidden md:block" style={{ left: '14%', top: '42%' }}>
              <div className="w-16 h-16 rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="/icon-512x512.png" 
                  alt="Nexus App" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Mobile PWA App Icon Hub - Bottom center */}
            <div className="absolute transform -translate-x-1/2 animate-float-subtle z-20 md:hidden" style={{ left: '42%', bottom: '-25%' }}>
              <div className="w-16 h-16 rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="/icon-512x512.png" 
                  alt="Nexus App" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Integration Icons Floating Above Wire Funnel */}
            {/* Desktop Layout */}
            <div className="hidden md:block">
              {/* Top row - above upper wire streams */}
              <div className="absolute top-12 animate-float-fast z-10" style={{ right: '35%' }}>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl">
                  {/* SendGrid Icon */}
                  <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="3" fill="#1A82E2"/>
                    <path d="M6 8h12v2H6V8zm0 3h12v2H6v-2zm0 3h8v2H6v-2z" fill="white"/>
                    <circle cx="18" cy="15" r="2" fill="#00D4AA"/>
                  </svg>
                </div>
              </div>
              
              <div className="absolute top-4 right-1/4 animate-float-medium z-10">
                <div className="w-20 h-20 rounded-full shadow-xl overflow-hidden">
                  {/* Gmail Icon */}
                  <img 
                    src="/gmail (2).png" 
                    alt="Gmail" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="absolute top-32 right-1/4 animate-float-fast z-10">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl">
                  {/* Twilio Icon */}
                  <svg className="h-12 w-12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#F22F46"/>
                    <circle cx="8.5" cy="8.5" r="2" fill="white"/>
                    <circle cx="15.5" cy="8.5" r="2" fill="white"/>
                    <circle cx="8.5" cy="15.5" r="2" fill="white"/>
                    <circle cx="15.5" cy="15.5" r="2" fill="white"/>
                  </svg>
                </div>
              </div>
              
              {/* Middle row - above center wire streams */}
              <div className="absolute top-1/2 left-1/3 transform -translate-y-1/2 -translate-y-8 animate-float-slow z-10">
                <div className="w-14 h-14 rounded-full shadow-xl overflow-hidden">
                  {/* Google Business Icon */}
                  <img 
                    src="/googlebusiness (2).png" 
                    alt="Google Business" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="absolute top-1/2 right-1/3 transform -translate-y-1/2 animate-float-fast z-10">
                <div className="w-12 h-12 rounded-full shadow-xl overflow-hidden">
                  {/* WhatsApp Icon */}
                  <img 
                    src="/whatsapp (2).png" 
                    alt="WhatsApp" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="absolute top-32 left-1/2 transform -translate-x-1/2 animate-float-slow z-10">
                <div className="w-20 h-20 rounded-full shadow-xl overflow-hidden">
                  {/* Meta Icon */}
                  <img 
                    src="/meta.png" 
                    alt="Meta" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Bottom row - above lower wire streams */}
              <div className="absolute bottom-16 left-1/4 animate-float-fast z-10">
                <div className="w-16 h-16 rounded-full shadow-xl overflow-hidden">
                  {/* Stripe Icon */}
                  <img 
                    src="/Stripe (2).png" 
                    alt="Stripe" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="absolute top-16 right-1/2 animate-float-slow z-10">
                <div className="w-22 h-22 bg-white rounded-full flex items-center justify-center shadow-xl">
                  {/* Google Analytics Icon */}
                  <svg className="h-12 w-12" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    <circle cx="12" cy="12" r="1" fill="white"/>
                  </svg>
                </div>
              </div>
              
              <div className="absolute bottom-8 right-1/3 animate-float-fast z-10">
                <div className="w-16 h-16 rounded-full shadow-xl overflow-hidden">
                  {/* ChatGPT Icon */}
                  <img 
                    src="/chat-gpt.png" 
                    alt="ChatGPT" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            
            {/* Mobile Layout - Icons arranged vertically */}
            <div className="md:hidden">
              {/* SendGrid - Top */}
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 animate-float-fast z-10">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-xl">
                  {/* SendGrid Icon */}
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="3" fill="#1A82E2"/>
                    <path d="M6 8h12v2H6V8zm0 3h12v2H6v-2zm0 3h8v2H6v-2z" fill="white"/>
                    <circle cx="18" cy="15" r="2" fill="#00D4AA"/>
                  </svg>
                </div>
              </div>
              
              {/* Gmail - Second row left */}
              <div className="absolute top-16 left-16 animate-float-medium z-10">
                <div className="w-16 h-16 rounded-full shadow-xl overflow-hidden">
                  <img src="/gmail (2).png" alt="Gmail" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* Google Analytics - Second row center */}
              <div className="absolute top-28 animate-float-slow z-10" style={{ left: '40%' }}>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl">
                  <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    <circle cx="12" cy="12" r="1" fill="white"/>
                  </svg>
                </div>
              </div>
              
              {/* Twilio - Third row left */}
              <div className="absolute top-40 left-20 animate-float-fast z-10">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-xl">
                  <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#F22F46"/>
                    <circle cx="8.5" cy="8.5" r="2" fill="white"/>
                    <circle cx="15.5" cy="8.5" r="2" fill="white"/>
                    <circle cx="8.5" cy="15.5" r="2" fill="white"/>
                    <circle cx="15.5" cy="15.5" r="2" fill="white"/>
                  </svg>
                </div>
              </div>
              
              {/* Meta - Third row right */}
              <div className="absolute top-40 right-24 animate-float-slow z-10">
                <div className="w-16 h-16 rounded-full shadow-xl overflow-hidden">
                  <img src="/meta.png" alt="Meta" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* Google Business - Fourth row left */}
              <div className="absolute top-60 left-28 animate-float-slow z-10">
                <div className="w-12 h-12 rounded-full shadow-xl overflow-hidden">
                  <img src="/googlebusiness (2).png" alt="Google Business" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* WhatsApp - Fourth row center */}
              <div className="absolute top-60 left-1/2 transform -translate-x-1/2 animate-float-fast z-10">
                <div className="w-10 h-10 rounded-full shadow-xl overflow-hidden">
                  <img src="/whatsapp (2).png" alt="WhatsApp" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* Stripe - Fifth row left */}
              <div className="absolute top-80 left-28 animate-float-fast z-10">
                <div className="w-14 h-14 rounded-full shadow-xl overflow-hidden">
                  <img src="/Stripe (2).png" alt="Stripe" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* ChatGPT - Fifth row right */}
              <div className="absolute top-80 right-28 animate-float-fast z-10">
                <div className="w-14 h-14 rounded-full shadow-xl overflow-hidden">
                  <img src="/chat-gpt.png" alt="ChatGPT" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Software Preview */}
      <section className="px-6 py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div 
            ref={headerRef}
            className={`text-center mb-12 transition-all duration-1000 ease-out ${
              headerVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See Nexus in
              <span className="block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                Action
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Real screenshots from our live software. This is what your business dashboard will look like.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              ref={card1Ref}
              className={`lg:col-span-1 transition-all duration-1000 ease-out ${
                card1Visible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
            >
              <KPIDashboardSnapshot />
            </div>
            <div 
              ref={card2Ref}
              className={`lg:col-span-1 transition-all duration-1000 ease-out ${
                card2Visible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
            >
              <CalendarSnapshot />
            </div>
            <div 
              ref={card3Ref}
              className={`lg:col-span-1 transition-all duration-1000 ease-out ${
                card3Visible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
            >
              <TeamSnapshot />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-3 py-6 lg:py-20 bg-gray-800/30">
        <div className="max-w-sm mx-auto lg:max-w-7xl">
          <div className="text-center mb-4 lg:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-2 lg:mb-6 leading-tight">
              Everything you need to
              <span className="block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                manage bookings
              </span>
            </h2>
            <p className="hidden lg:block text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 lg:max-w-3xl lg:mx-auto leading-relaxed">
              From appointment scheduling to team management, we've got you covered with powerful features designed for modern businesses.
            </p>
          </div>

          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-16 lg:space-y-0 lg:items-start">
            {/* Feature Cards */}
            <div className="space-y-3 lg:space-y-12">
              {/* Feature 1 */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 lg:p-8">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 lg:w-12 lg:h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-md lg:rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-3 w-3 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-4">Smart Scheduling</h3>
                    <p className="hidden lg:block text-xs lg:text-base text-gray-300 mb-2 lg:mb-6 leading-relaxed">
                      Intelligent calendar management with automated scheduling, conflict detection, and seamless booking flows.
                    </p>
                    <ul className="space-y-1 lg:space-y-2">
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Automated online scheduling</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Conflict detection</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Mobile calendar sync</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 2 - Sales & Marketing */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 lg:p-8">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 lg:w-12 lg:h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-md lg:rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-3 w-3 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-4">Sales & Marketing</h3>
                    <p className="hidden lg:block text-xs lg:text-base text-gray-300 mb-2 lg:mb-6 leading-relaxed">
                      Comprehensive sales tools and marketing automation to grow your business and convert more leads.
                    </p>
                    <ul className="space-y-1 lg:space-y-2">
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Automated lead management</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Advanced proposals</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Email & SMS campaigns</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Customer engagement automations</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 3 - Team Management */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 lg:p-8">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 lg:w-12 lg:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-md lg:rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="h-3 w-3 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-4">Team Management</h3>
                    <p className="hidden lg:block text-xs lg:text-base text-gray-300 mb-2 lg:mb-6 leading-relaxed">
                      Comprehensive team coordination with role-based permissions, time tracking, and performance analytics.
                    </p>
                    <ul className="space-y-1 lg:space-y-2">
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Role-based access</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Time clock, flat rate, commission and bonus tracking</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Performance insights</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Field GPS tracking</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 4 - Powerful Supplies and Inventory Management */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 lg:p-8">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 lg:w-12 lg:h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-md lg:rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-3 w-3 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-4">Supplies & Inventory Management</h3>
                    <p className="hidden lg:block text-xs lg:text-base text-gray-300 mb-2 lg:mb-6 leading-relaxed">
                      Complete inventory control with real-time tracking, automated reordering, and comprehensive supply chain management.
                    </p>
                    <ul className="space-y-1 lg:space-y-2">
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Real-time inventory tracking</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Automated reorder alerts</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Supplier management</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Cost tracking and optimization</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Feature 5 - Analytics & Reports */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 lg:p-8">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 lg:w-12 lg:h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-md lg:rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-3 w-3 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-4">Analytics & Reports</h3>
                    <p className="hidden lg:block text-xs lg:text-base text-gray-300 mb-2 lg:mb-6 leading-relaxed">
                      Powerful business insights with real-time dashboards, custom reports, and predictive analytics.
                    </p>
                    <ul className="space-y-1 lg:space-y-2">
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Real-time dashboards</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Custom reports</span>
                      </li>
                      <li className="flex items-center text-sm lg:text-sm text-gray-300">
                        <CheckCircle className="h-2 w-2 lg:h-4 lg:w-4 text-green-400 mr-1 lg:mr-2 flex-shrink-0" />
                        <span>Predictive insights</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Live Software Snapshots - Desktop Only */}
            <div className="hidden lg:block space-y-8">
              <div className="grid gap-6">
                <MessagesSnapshot />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ProposalsSnapshot />
                  <CallingSnapshot />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Why businesses choose
                <span className="block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                  Nexus
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                We're not just another booking system. We're your partner in business growth, 
                providing the tools and insights you need to scale efficiently.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Lightning Fast</h3>
                    <p className="text-gray-300">Built for speed with modern technology that keeps your business moving.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Enterprise Security</h3>
                    <p className="text-gray-300">Bank-level security with end-to-end encryption and compliance standards.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Global Scale</h3>
                    <p className="text-gray-300">Trusted by businesses worldwide with 99.9% uptime and 24/7 support.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-3xl p-8 backdrop-blur-xl border border-gray-700">
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Today's Bookings</div>
                        <div className="text-sm text-gray-400">24 appointments</div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-green-400">+12%</div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Team Performance</div>
                        <div className="text-sm text-gray-400">8 team members</div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-green-400">98%</div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">Revenue Growth</div>
                        <div className="text-sm text-gray-400">This month</div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-green-400">+28%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to transform your
            <span className="block bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              booking experience?
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Join thousands of businesses already using Nexus to streamline their operations and delight their customers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <span>Start Your Free Trial</span>
              <ArrowRight className="h-5 w-5" />
            </button>
            <button
              onClick={handleSignIn}
              className="px-8 py-4 bg-gray-800/50 border border-gray-700 rounded-full text-white font-semibold hover:bg-gray-700/50 transition-all duration-300"
            >
              Sign In to Your Account
            </button>
          </div>
          
          <p className="text-sm text-gray-400 mt-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <span className="text-xl font-bold">Nexus</span>
              </div>
              <p className="text-gray-400 text-sm">
                The ultimate booking management system for modern businesses.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Nexus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Website;
